import dayjs from 'dayjs';
import { heicTo, isHeic } from 'heic-to';
import { AddressProps, FileType } from '../../pages/Applicant/Applicant.types';
import { EVENT_LOG_NAMES, honeybadgerLogEvent } from './logging';

export const emDash = '—';

// Some reviewers' Okta identities still end in the legacy @tbkbank.com domain;
// rewrite an exact trailing match to the canonical @tfin.com (ORIG-742).
export const normalizeInternalEmail = (email: string): string =>
    email.replace(/@tbkbank\.com$/i, '@tfin.com');

export const removeWhiteSpaces = (str: string) => str.trim().split(/\s+/).join(' ');

export const formatAddress = ({
    line1,
    line2,
    city,
    state,
    zip,
}: Omit<AddressProps, 'stateOrTerritory'> & { state: string }) => {
    if (line1 && city && state && zip) {
        const address = [];
        let addressLine = line1;

        if (line2) {
            addressLine += line2;
        }

        const filteredAddressLine = removeWhiteSpaces(addressLine);
        const stateWithZip = removeWhiteSpaces(`${state} ${zip}`);
        address.push(filteredAddressLine);
        address.push(city);
        address.push(stateWithZip);

        const isAddressEmpty = address.filter((item) => item).length === 0;

        return !isAddressEmpty ? address.join(', ') : '';
    } else {
        return emDash;
    }
};

export const formatName = ({
    firstName,
    middleName,
    lastName,
}: {
    firstName: string;
    middleName?: string;
    lastName: string;
}) => {
    let fullName = firstName;
    if (middleName) {
        fullName += ` ${middleName}`;
    }
    fullName += ` ${lastName}`;

    return fullName;
};

export const formatPhoneNumber = (phoneNumber: string) => {
    if (phoneNumber) {
        const cleaned = ('' + phoneNumber).replace(/\D/g, '');
        const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
        if (match) {
            return '(' + match[1] + ') ' + match[2] + '-' + match[3];
        }

        const formattedNumber = `(${phoneNumber.slice(
            0,
            3,
        )}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6)}`;
        return formattedNumber;
    } else {
        return emDash;
    }
};

export const formatEIN = (ein: string) => {
    if (ein) {
        const groupedStr = ein.match(/^([\d*]{2})([\d*]{7})$/);

        return groupedStr?.length === 3 ? `${groupedStr[1]}-${groupedStr[2]}` : emDash;
    } else {
        return emDash;
    }
};

const ORDINAL_SUFFIXES: Record<number, string> = { 1: 'st', 2: 'nd', 3: 'rd' };

export const formatOrdinal = (value: number) => {
    const magnitude = Math.abs(Math.trunc(value));
    // 11/12/13 — and every hundred thereafter — take 'th' despite their ones digit.
    const isTeen = magnitude % 100 >= 11 && magnitude % 100 <= 13;

    return `${Math.trunc(value)}${isTeen ? 'th' : (ORDINAL_SUFFIXES[magnitude % 10] ?? 'th')}`;
};

export const formatDate = (date: string | Date, formatTime?: boolean) => {
    const newDate = new Date(date);
    if (date && !isNaN(newDate.getTime())) {
        const newDate = new Date(date);
        const time = newDate.toLocaleTimeString([], {
            hour: 'numeric',
            minute: '2-digit',
        });
        const month = (newDate.getMonth() + 1).toString().padStart(2, '0');
        const day = newDate.getDate().toString().padStart(2, '0');
        const year = newDate.getFullYear();

        return formatTime ? `${month}/${day}/${year} ${time}` : `${month}/${day}/${year}`;
    } else {
        return emDash;
    }
};

export const getDateFromUtcString = (dateStr: string): Date => {
    const isAmbiguous = !/(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(dateStr);

    return new Date(isAmbiguous ? `${dateStr}Z` : dateStr);
};

export const formatDateWithTimezone = (
    date: string,
    options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        timeZoneName: 'short',
    },
) => {
    if (!date) {
        honeybadgerLogEvent(EVENT_LOG_NAMES.Exception, {
            level: 'WARN',
            customAttributes: { message: 'formatDateWithTimezone called with empty date' },
        });
        return emDash;
    }

    const formattedDate = getDateFromUtcString(date);
    if (isNaN(formattedDate.getTime())) {
        honeybadgerLogEvent(EVENT_LOG_NAMES.Exception, {
            level: 'WARN',
            customAttributes: { message: 'formatDateWithTimezone called with invalid date', date },
        });
        return emDash;
    }

    const formatter = new Intl.DateTimeFormat('en-US', options);

    return formatter.format(formattedDate).replace(',', '');
};

export const formatSSN = (ssn: string) => {
    if (ssn) {
        const groupedStr = ssn.match(/^([\d*]{3})([\d*]{2})([\d*]{4})$/);

        return groupedStr?.length === 4
            ? `${groupedStr[1]}-${groupedStr[2]}-${groupedStr[3]}`
            : emDash;
    } else {
        return emDash;
    }
};

export const convertHeicToPng = async (file: File) => {
    const isFileHeic = await isHeic(file);

    if (!isFileHeic) return file;

    try {
        const convertedBlob = await heicTo({
            blob: file,
            type: 'image/png',
            quality: 0.9,
        });
        const newFilename = file.name.replace(/\.heic$/i, '.png');

        return new File([convertedBlob as BlobPart], newFilename, {
            type: 'image/png',
        });
    } catch (error) {
        console.log('HEIC conversion failed', error);
        return file;
    }
};

export const maskValue = (value: string): string => value.replace(/[A-Za-z0-9]/g, '*');

export const removeDateTzOffset = (date: string | Date) => {
    const dayjsDate = dayjs(date);
    if (!dayjsDate.isValid()) {
        return undefined;
    }
    if (typeof date === 'string') {
        return date.replace(/(\+|-)\d{2}:\d{2}$|Z$/, '');
    }
    return dayjsDate.format('MM/DD/YYYY');
};

export const coerceStringToDateNoTime = (date?: string) => {
    if (!date) {
        return undefined;
    }
    const defaultDateString = date ? removeDateTzOffset(date) : undefined;
    return defaultDateString ? new Date(defaultDateString) : undefined;
};

interface FileTypeOption {
    label: string;
    value: FileType;
}

interface FileTypeOptionGroup {
    label: string;
    items: FileTypeOption[];
}

export const FILE_TYPE_OPTION_GROUPS: FileTypeOptionGroup[] = [
    {
        label: 'Identity',
        items: [
            { label: 'ID Passport', value: FileType.ID_Passport },
            { label: 'Voided Check', value: FileType.VoidedCheck },
            {
                label: 'Bank Statement / Bank Letter (Direct Deposit Form)',
                value: FileType.BankDocument,
            },
            { label: 'Certificate of Insurance', value: FileType.CertificateOfInsurance },
            { label: 'Drivers License - Front', value: FileType.ID_Front },
            { label: 'Drivers License - Back', value: FileType.ID_Back },
            { label: 'Drivers License - Front & Back', value: FileType.ID_FrontAndBack },
            { label: 'CLEAR Report (Thomson Reuters CLEAR)', value: FileType.Clear },
            { label: 'Other', value: FileType.Other },
        ],
    },
    {
        label: 'Business Formation',
        items: [
            {
                label: 'Articles of Organization / Articles of Incorporation (Certificate of Formation)',
                value: FileType.ArticlesOfOrganization,
            },
            {
                label: 'Certificate of Good Standing (Certificate of Existence, Certificate of Status)',
                value: FileType.CertificateOfGoodStanding,
            },
            {
                label:
                    'Operating Agreement (Partnership Agreement, Bylaws, Corporate Resolution, Meeting Minutes)',
                value: FileType.OperatingAgreement,
            },
            {
                label:
                    'Assumed Name Certificate (Fictitious Business Name Statement, Trade Name Certificate, DBA Filing)',
                value: FileType.AssumedNameCertificate,
            },
            {
                label: 'Amendment to Articles (Statement of Change, Statement of Information)',
                value: FileType.AmendmentToArticles,
            },
            {
                label: 'Certificate of Reinstatement (Certificate of Revival)',
                value: FileType.CertificateOfReinstatement,
            },
        ],
    },
    {
        label: 'Tax',
        items: [
            {
                label:
                    'FEIN Tax Letter (IRS Form SS-4, IRS Letter 147C, IRS Form CP575, EIN Assignment Letter)',
                value: FileType.FeinTaxLetter,
            },
            { label: 'Social Security Card (SSN Card)', value: FileType.SocialSecurityCard },
            {
                label: 'ITIN Card (Individual Taxpayer Identification Number Card, IRS Letter CP565)',
                value: FileType.ItinCard,
            },
            {
                label: 'IRS Form W-9 (Request for Taxpayer Identification Number)',
                value: FileType.IrsFormW9,
            },
            { label: 'Work Visa (U.S. Work Visa)', value: FileType.WorkVisa },
        ],
    },
    {
        label: 'Address',
        items: [
            {
                label: 'Utility Bill (Electric Bill, Gas Bill, Water Bill)',
                value: FileType.UtilityBill,
            },
            {
                label: 'Phone Statement (Cell Phone Bill, Wireless Statement)',
                value: FileType.PhoneStatement,
            },
        ],
    },
    {
        label: 'Contracts',
        items: [
            {
                label: 'Letter of Release (UCC-3 Termination Statement, Release Letter)',
                value: FileType.LetterOfRelease,
            },
            { label: 'UCC Filing (UCC-1 Financing Statement)', value: FileType.UccFiling },
            {
                label: 'Competitor Factoring Agreement (Current Factor Agreement)',
                value: FileType.CompetitorFactoringAgreement,
            },
            {
                label: 'Buy/Sell Agreement (Bill of Sale, Asset Purchase Agreement)',
                value: FileType.BuySellAgreement,
            },
            {
                label: 'Lease Agreement (Equipment Lease, Truck Lease, Trailer Lease)',
                value: FileType.LeaseAgreement,
            },
            { label: 'Owner/Operator Agreement', value: FileType.OwnerOperatorAgreement },
            { label: 'Master Service Agreement (MSA)', value: FileType.MasterServiceAgreement },
        ],
    },
    {
        label: 'Invoice Documents',
        items: [
            {
                label: 'Invoice Package (Invoice + BOL + Rate Confirmation + Remittance Advice)',
                value: FileType.InvoicePackage,
            },
            { label: 'Invoice (Freight Invoice, Freight Bill)', value: FileType.Invoice },
            { label: 'Bill of Lading (BOL)', value: FileType.BillOfLading },
            {
                label: 'Rate Confirmation (Rate Con, Load Confirmation, Carrier Confirmation)',
                value: FileType.RateConfirmation,
            },
            {
                label: 'Proof of Delivery (POD, Signed Delivery Receipt)',
                value: FileType.ProofOfDelivery,
            },
            { label: 'Remittance Advice', value: FileType.RemittanceAdvice },
        ],
    },
    {
        label: 'Carrier Authority',
        items: [
            {
                label: 'FMCSA Authority Letter (MC Authority Letter, Operating Authority Printout)',
                value: FileType.FmcsaAuthorityLetter,
            },
        ],
    },
    {
        label: 'Internal Diligence Reports',
        items: [
            { label: 'UCC Search Results (UCC Lien Search)', value: FileType.UccSearchResults },
            {
                label: 'Middesk Business Report (KYB Report)',
                value: FileType.MiddeskBusinessReport,
            },
            {
                label: 'OFAC Search Results (Sanctions Screening)',
                value: FileType.OfacSearchResults,
            },
            { label: 'Risk Inform Report', value: FileType.RiskInformReport },
            { label: 'Notice of Assignment (NOA)', value: FileType.NoticeOfAssignment },
            {
                label: 'Secretary of State Entity Search Printout (Corp Search)',
                value: FileType.SecretaryOfStateEntitySearch,
            },
        ],
    },
];

export const FILE_TYPE_OPTIONS: FileTypeOption[] = FILE_TYPE_OPTION_GROUPS.flatMap(
    (group) => group.items,
);

// The applicant-documents API returns documentType as the FileType enum *key*
// ("CertificateOfInsurance", "ID_Back"), not the numeric value the dialog uses.
// Accept either form so a single helper covers the table column AND the
// attached-files chip in conditions.
export const getFileTypeLabel = (type: FileType | string | null | undefined): string => {
    if (type == null) return 'Other';
    const numericType =
        typeof type === 'string' ? FileType[type as keyof typeof FileType] : type;
    return FILE_TYPE_OPTIONS.find((o) => o.value === numericType)?.label ?? 'Other';
};
